/* eslint-disable ember/no-incorrect-calls-with-inline-anonymous-functions, ember/no-runloop */
import { isDestroyed, isDestroying } from '@ember/destroyable';
import { fn, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { action, set } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didInsert, didUpdate } from '@ember/render-modifiers';
import { debounce, scheduleOnce } from '@ember/runloop';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismIcon,
  PrismInput,
  PrismLoadingBar,
  PrismPagination,
} from '@mylighthouse/prism-ember';
import EmberPopover from '@mylighthouse/prism-ember/components/ember-popover';
// @ts-expect-error: Incorrect type
import preventDefault from 'ember-event-helpers/helpers/prevent-default';
import { t } from 'ember-intl';
import { eq, gt, or } from 'ember-truth-helpers';
import GeneralEventName from 'lumen/components/general/event-name';
import HotelEventsTooltipsTooltipEvent from 'lumen/components/hotel/events/tooltips/tooltip-event';
import type { SearchResults } from 'lumen/controllers/hotel/events';
import dayjsFormat from 'lumen/helpers/dayjs-format';
import type { Event as EventModel } from 'lumen/models';

interface HotelEventsFormFieldsEventSearchFieldSignature {
  Args: {
    addExistingEvents: (event: { added: boolean; id: string }) => void;
    fetchSearchResult: (searchEvent: string, page: number) => void;
    onToggleSearch: () => void;
    resetEvents: () => void;
    searchResults: SearchResults;
  };
}

export default class HotelEventsFormFieldsEventSearchField extends Component<HotelEventsFormFieldsEventSearchFieldSignature> {
  @tracked hasAddedEvents = false;
  @tracked isOpen = false;
  @tracked page = 1;
  @tracked searchEvent = '';

  prevSearchEvent: string | null = null;
  privateOutsideClickHandler: ((e: MouseEvent) => void) | null = null;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get total() {
    if (this.args.searchResults?.total) {
      return this.args.searchResults?.total;
    }
    return 1;
  }

  willDestroy(): void {
    super.willDestroy();

    this.removeOutsideClickHandler();
  }

  debouncedSend(): void {
    this.args.fetchSearchResult(this.searchEvent, this.page);
  }

  initialFetch(): void {
    this.hasAddedEvents = false;

    // Trigger initial search when the search field becomes active
    const event = {
      target: {
        value: '',
      },
    };

    // @ts-expect-error: Incorrect type
    this.fetchSearchFromController(event);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  outsideClickHandler(e: MouseEvent) {
    if (this.isOpen && e.target instanceof Element) {
      // Alternative found online:
      // https://github.com/zeppelin/ember-click-outside/blob/master/addon/mixin.js

      // Element.closest() behavior:
      // Starting with the Element itself, the closest() method traverses parents(heading toward the document root) of the Element
      // until it finds a node that matches the provided selectorString.
      // Will return itself or the matching ancestor.If no such element exists, it returns null.

      // The closest element should be the boundary of the region where we want to detect the inside click.
      // If the .closest() returns null, this means that the click target isn't a child element of the inside click region.
      let isInside = false;

      if (
        // The target could already be removed from the DOM (f.e. the icon gets replaced when clicking on it), in that case we assume we clicked inside
        !e.target.ownerDocument.body.contains(e.target) ||
        e.target.closest('.dropdown') !== null ||
        e.target.closest('.search-input') !== null ||
        e.target.closest('.pagination-container') !== null ||
        e.target.closest('.pagination-button') !== null ||
        e.target.closest('.events-calendar-popover') !== null ||
        (typeof e.target.className?.indexOf === 'function' &&
          e.target.className.indexOf('do-not-close-on-outside-click') !== -1)
      ) {
        isInside = true;
      }

      if (!isInside) {
        this.toggleDropdown();
      }
    }
  }

  removeOutsideClickHandler(): void {
    if (this.privateOutsideClickHandler) {
      document.removeEventListener('click', this.privateOutsideClickHandler);
    }
  }

  toggleDropdown(): void {
    if (isDestroyed(this) || isDestroying(this)) {
      return;
    }

    this.isOpen = !this.isOpen;

    if (this.isOpen) {
      // http://discuss.emberjs.com/t/solved-how-to-remove-event-handler-properly-in-a-component/8931/2
      this.privateOutsideClickHandler = this.outsideClickHandler.bind(this);
      document.addEventListener('click', this.privateOutsideClickHandler);
    } else {
      // Handle adding all events before closing and reset search results
      if (this.hasAddedEvents) {
        this.args.resetEvents();
      }
      this.removeOutsideClickHandler();
      this.searchEvent = '';
      this.prevSearchEvent = null;
      this.args.onToggleSearch();
    }
  }

  @action
  addEvent(event: { added: boolean; id: string }): void {
    this.hasAddedEvents = true;
    this.args.addExistingEvents(event);
  }

  @action
  addOrRemoveSingleEvent(
    event: EventModel,
    added: boolean,
    className: string,
  ): void {
    set(event, 'added', added);
    set(event, 'declined', !added);
    // @ts-expect-error: Argument of type 'EventModel' is not assignable to parameter of type '{ id: string; added: boolean; }'.
    this.addEvent(event);
    // An extra property so the hover doesn't cause the class to change immediately
    set(event, 'justAdded', true);
    set(event, 'class', className);
  }

  @action
  debouncedToggleDropdown(): void {
    /*
      Needs to be debounced because paper input onfocus is sometimes called multiple times
      Can't toggle when the dropdown is open because this would close the dropdown
      This would cause the togglesearch to be called and remove the input field to disappear
    */

    if (!this.isOpen) {
      // eslint-disable-next-line @typescript-eslint/unbound-method
      debounce(this, this.toggleDropdown, 500, false);
    }
  }

  @action
  didInsertSearchFieldElement(): void {
    scheduleOnce('afterRender', this, () => this.initialFetch());
  }

  @action
  eventEnter(event: EventModel, className: string): void {
    if (!event.justAdded) {
      set(event, 'class', className);
    }
  }

  @action
  eventLeave(event: EventModel, className: string): void {
    set(event, 'justAdded', false);
    set(event, 'class', className);
  }

  @action
  fetchSearchFromController(event: Event): void {
    const input = (event.target as HTMLInputElement).value;
    this.searchEvent = input;
    // Reset the page when a new search is made
    this.page = 1;

    if (
      (this.searchEvent?.length >= 3 || this.searchEvent?.length === 0) &&
      this.searchEvent !== this.prevSearchEvent
    ) {
      this.prevSearchEvent = input;
      // eslint-disable-next-line @typescript-eslint/unbound-method
      debounce(this, this.debouncedSend, 300);
    }
  }

  @action
  onMouseEnter(event: EventModel): void {
    set(event, 'isPopoverActive', true);
  }

  @action
  onMouseLeave(event: EventModel): void {
    set(event, 'isPopoverActive', false);
  }

  @action
  onPageChange(): void {
    this.args.fetchSearchResult(this.searchEvent, this.page);
  }

  @action
  setPage(page: number): void {
    this.page = page;
  }

  @action
  toggleSearch(): void {
    if (this.isOpen) {
      this.toggleDropdown();
    } else {
      this.args.onToggleSearch();
    }
  }

  <template>
    {{! template-lint-disable no-nested-interactive }}
    <div
      class="search-field"
      {{didInsert this.didInsertSearchFieldElement}}
      {{didUpdate this.onPageChange this.page}}
    >
      <div class="search-input flex flex-col">
        <PrismInput
          @autofocus={{true}}
          @iconAfter={{hash name="search"}}
          @onFocusIn={{this.debouncedToggleDropdown}}
          @onInput={{this.fetchSearchFromController}}
          @placeholder={{t "general.search_placeholder"}}
          @type="text"
          @value={{this.searchEvent}}
        />
      </div>
      <button
        class="button--unstyled ml-100"
        type="button"
        {{on "click" (preventDefault this.toggleSearch)}}
      >
        <PrismIcon @color="default" @name="cross" @size="small" />
      </button>
      {{#if this.isOpen}}
        <div class="dropdown">
          {{#if @searchResults.loading}}
            <PrismLoadingBar />{{t "general.loading"}}
          {{else if (gt @searchResults.events.length 0)}}
            <div class="events-result">
              <table class="event-suggestions">
                <thead>
                  <tr class="event-row">
                    <th class="event-cell truncate">
                      {{!t "events.add_to_calendar"}}
                    </th>
                    <th class="event-cell truncate">
                      {{t "general.date"}}
                    </th>
                    <th class="event-cell truncate">
                      {{t "general.name"}}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {{#each @searchResults.events as |result|}}
                    <tr
                      class="event-row"
                      role="tooltip"
                      {{on "mouseenter" (fn this.onMouseEnter result)}}
                      {{on "mouseleave" (fn this.onMouseLeave result)}}
                    >
                      <td class="event-cell truncate pr-200 pl-200">
                        {{#if (or result.added (eq result.status "accepted"))}}
                          <button
                            class="button--unstyled events-suggestions-button added-event-icon do-not-close-on-outside-click event-suggestions-button--with-prism-icon"
                            type="button"
                            {{on
                              "click"
                              (preventDefault
                                (fn
                                  this.addOrRemoveSingleEvent
                                  result
                                  false
                                  "cross"
                                )
                              )
                            }}
                            {{on
                              "mouseenter"
                              (fn this.eventEnter result "cross")
                            }}
                            {{on
                              "mouseleave"
                              (fn this.eventLeave result "checkmark")
                            }}
                          >
                            {{#if (eq result.class "cross")}}
                              <PrismIcon
                                @color="critical"
                                @name="cross"
                                @size="extra-small"
                              />
                            {{else}}
                              <PrismIcon
                                @color="success"
                                @name="checkmark"
                                @size="extra-small"
                              />
                            {{/if}}
                          </button>
                        {{else if
                          (or result.declined (eq result.status "rejected"))
                        }}
                          <button
                            class="button--unstyled events-suggestions-button added-event-icon do-not-close-on-outside-click event-suggestions-button--with-prism-icon"
                            type="button"
                            {{on
                              "click"
                              (preventDefault
                                (fn
                                  this.addOrRemoveSingleEvent
                                  result
                                  true
                                  "checkmark"
                                )
                              )
                            }}
                            {{on
                              "mouseenter"
                              (fn this.eventEnter result "checkmark")
                            }}
                            {{on
                              "mouseleave"
                              (fn this.eventLeave result "cross")
                            }}
                          >
                            {{#if (eq result.class "cross")}}
                              <PrismIcon
                                @color="critical"
                                @name="cross"
                                @size="extra-small"
                              />
                            {{else}}
                              <PrismIcon
                                @color="success"
                                @name="checkmark"
                                @size="extra-small"
                              />
                            {{/if}}
                          </button>
                        {{else}}
                          <button
                            class="button--unstyled events-suggestions-button event-suggestions-button--with-prism-icon do-not-close-on-outside-click"
                            type="button"
                            {{on
                              "click"
                              (preventDefault
                                (fn
                                  this.addOrRemoveSingleEvent
                                  result
                                  true
                                  "checkmark"
                                )
                              )
                            }}
                          >
                            <PrismIcon
                              @color="info"
                              @name="plus"
                              @size="extra-small"
                            />
                          </button>
                        {{/if}}
                      </td>
                      <td
                        class="events-suggestions-item search-date-field event-cell truncate"
                      >
                        {{#if (eq result.numberOfDays 1)}}
                          {{dayjsFormat result.startDate format="L"}}
                        {{else}}
                          {{dayjsFormat result.startDate format="L"}}
                          &nbsp;-&nbsp;
                          {{dayjsFormat result.endDate format="L"}}
                        {{/if}}
                      </td>
                      <td
                        class="events-suggestions-item search-name-field event-cell truncate"
                      >
                        <GeneralEventName @event={{result}} />
                      </td>
                      {{!isShown=result.isPopoverActive}}
                      <EmberPopover
                        @delay={{100}}
                        @event="hover"
                        @hideDelay={{100}}
                        @side="left"
                        @tooltipClass="events-calendar-popover"
                      >
                        <HotelEventsTooltipsTooltipEvent @event={{result}} />
                      </EmberPopover>
                    </tr>
                  {{/each}}
                </tbody>
              </table>
            </div>
            <div class="pagination-row-container">
              <PrismPagination
                @lastPage={{this.total}}
                @page={{this.page}}
                @setPage={{this.setPage}}
              />
            </div>
          {{else}}
            {{t "general.no_results"}}
          {{/if}}
        </div>
      {{/if}}
    </div>
  </template>
}
